'use client';

import Link from '@eliadmin/core/src/components/link';

import type {
  CategoriesEntity,
  GeneralLayout,
} from '@eliadmin/core/src/api/general/types/general-layout-types';

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@eliadmin/core/src/components/ui/sheet';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';

import { LuMenu, LuPlus, LuX } from 'react-icons/lu';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { isServicesMenuItem } from './menu-utils';

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
  generalLayout: GeneralLayout['data'];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const { t } = useTranslations();
  const translatedMenu = t('text3241')?.trim();
  const menuLabel = translatedMenu && translatedMenu !== 'text3241' ? translatedMenu : 'Menu';
  const translatedClose = t('text272')?.trim();
  const closeLabel = translatedClose && translatedClose !== 'text272' ? translatedClose : 'Close';

  return (
    <>
      <Sheet>
        <SheetTrigger
          aria-label={menuLabel}
          className={cn('text-black', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          <LuMenu size={24} />
        </SheetTrigger>
        <SheetContent
          aria-describedby={undefined}
          side={props.side}
          showCloseIcon={false}
          className={cn(
            'flex w-full flex-col items-start justify-start gap-0 border-none p-0',
            'ea-mobile-sidebar__content',
            props.className?.content,
          )}
        >
          <SheetTitle className="sr-only">{menuLabel}</SheetTitle>
          <div className="flex h-[52px] w-full items-center justify-start px-4">
            <SheetClose aria-label={closeLabel}>
              <LuX size={24} color="black" />
            </SheetClose>
          </div>
          <div className={cn('h-[1px] w-full', 'ea-header__divider')} />
          <div className="flex w-full flex-col items-start justify-start gap-8 p-4">
            <div className="flex w-full flex-col items-center justify-center gap-4">
              {props.menu &&
                props.menu.length > 0 &&
                props.menu.map(item => (
                  <SheetClose asChild className="text-center text-lg text-black" key={item.name}>
                    <Link prefetchOnHover prefetch={false} href={getSafeHref(item?.path ?? '')}>
                      {item.name}
                      {isServicesMenuItem(item.path) && (
                        <sup className="text-[12px] font-semibold text-primary">5</sup>
                      )}
                    </Link>
                  </SheetClose>
                ))}
              <SheetClose asChild className="w-full">
                <Link prefetchOnHover
                  prefetch={false}
                  href="/konfigurator"
                  className="flex h-12 w-full items-center justify-center gap-2 border-b-2 border-b-black bg-primary text-base font-medium text-white"
                >
                  <LuPlus size={20} /> {t('text818')}
                </Link>
              </SheetClose>
            </div>
            <div className={cn('h-[1px] w-full', 'ea-header__divider')} />
            <div className="flex w-full flex-col items-center justify-center gap-4 text-center">
              <LabelValue label={t('text800')} value={props.generalLayout.contact?.phone ?? ''} />
              <LabelValue label="Napíšte nám" value={props.generalLayout.contact?.email ?? ''} />
              <LabelValue label={t('text3592')} value={'PO-PIA: 8:00 - 16:00'} />
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

function LabelValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex w-full flex-col items-center justify-center gap-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm">{value}</p>
    </div>
  );
}

export default MobileSidebar;
